/* assets/custom-pdp.js */

class CustomPDP extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;

    // Selectors
    this.qtyBlock = this.querySelector('#cpdp-qty-block');
    this.hiddenQtyInput = this.querySelector('#cpdp-hidden-qty');
    this.atcBtn = this.querySelector('#cpdp-atc-btn');
    this.atcPriceSpan = this.querySelector('#cpdp-atc-price');
    this.sellingPlanInput = this.querySelector('#cpdp-selling-plan');

    // 1. Base Price (One-time) - from data attribute for accuracy
    this.oneTimePriceCents = parseInt(this.dataset.productPrice, 10) || 0;

    // 2. Parse selling plan data from Liquid
    this.sellingPlans = [];
    try {
      this.sellingPlans = JSON.parse(this.dataset.sellingPlans || '[]');
    } catch (e) {
      console.warn('Could not parse selling plans data');
    }

    // Initial State
    this.currentPriceCents = this.oneTimePriceCents;
    this.currentQty = 1;
    this.currentSellingPlanId = ''; // Empty = one-time purchase

    // Free shipping threshold (in cents) - configurable via section settings
    this.freeShippingThreshold = parseInt(this.dataset.freeShippingThreshold, 10) || 5000;

    // Automatic discount percentage - used as fallback when cart is empty
    this.automaticDiscountPercent = parseInt(this.dataset.automaticDiscountPercent, 10) || 0;

    // Initialization
    this.initMediaGallery();
    this.initQuantitySelector();
    this.initPurchaseOptions();
    this.initAccordions();
    this.initAddToCart();
    this.checkCartState();
    this.checkFreeShippingBadges();

    // Listen for cart changes from slide cart (for page refresh on item removal)
    this.boundCartChangeHandler = this.handleCartChange.bind(this);
    document.addEventListener('cart:changed', this.boundCartChangeHandler);
  }

  disconnectedCallback() {
    document.removeEventListener('cart:changed', this.boundCartChangeHandler);
  }

  // --- HANDLE CART CHANGES (for conditional sections refresh) ---
  handleCartChange(event) {
    // Re-check cart state when items change in slide cart
    this.checkCartState();
    // Re-check free shipping badges in case discount percentage changed
    this.checkFreeShippingBadges();
    // Update Add to Cart button to reflect current selection
    this.updateATCButton();
  }

  parsePrice(el) {
    if (!el || !el.innerText) return 0;
    const cleanString = el.innerText.replace(/[^0-9.]/g, '');
    return parseFloat(cleanString) * 100;
  }

  // --- CART LOGIC (Hide Qty if > $50) ---
  async checkCartState() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      const threshold = 5000; // $50.00

      if (cart.total_price >= threshold) {
        if (this.qtyBlock) this.qtyBlock.style.display = 'none';
      } else {
        if (this.qtyBlock) {
          this.qtyBlock.style.display = 'block';
          // Note: Don't set .cpdp-qty-tag visibility here - that's handled by checkFreeShippingBadges()
        }
      }
    } catch (error) {
      console.error('Error fetching cart state:', error);
    }
  }

  // --- FREE SHIPPING BADGE LOGIC ---
  async checkFreeShippingBadges() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      // Calculate discount percentage from automatic discounts in cart
      let discountPercentage = 0;

      if (cart.cart_level_discount_applications && cart.cart_level_discount_applications.length > 0) {
        // Calculate effective discount percentage from cart data
        // total_discount / (total_price + total_discount) * 100
        const originalTotal = cart.total_price + cart.total_discount;
        if (originalTotal > 0) {
          discountPercentage = (cart.total_discount / originalTotal) * 100;
        }
      }

      // If no discount detected from cart, use the configured fallback
      // (handles empty cart scenario when automatic discounts are active)
      if (discountPercentage === 0 && this.automaticDiscountPercent > 0) {
        discountPercentage = this.automaticDiscountPercent;
      }

      // Get all qty buttons with FREE SHIPPING tags
      const qtyBtns = this.querySelectorAll('.cpdp-qty-btn[data-qty]');
      const productPrice = this.oneTimePriceCents;

      qtyBtns.forEach(btn => {
        const qty = parseInt(btn.dataset.qty, 10);
        const tag = btn.querySelector('.cpdp-qty-tag');

        if (!tag) return; // No FREE SHIPPING tag on this button

        // Calculate total for this qty
        const subtotalCents = productPrice * qty;

        // Apply discount if any
        const discountedTotal = discountPercentage > 0
          ? subtotalCents * (1 - discountPercentage / 100)
          : subtotalCents;

        // Show/hide tag based on whether it meets threshold
        if (discountedTotal >= this.freeShippingThreshold) {
          tag.style.display = '';
        } else {
          tag.style.display = 'none';
        }
      });

    } catch (error) {
      console.error('Error checking free shipping badges:', error);
    }
  }

  // --- UPDATE ATC BUTTON & TOTALS ---
  updateATCButton() {
    if (!this.atcPriceSpan) return;
    const totalCents = this.currentPriceCents * this.currentQty;
    this.atcPriceSpan.innerText = this.formatMoney(totalCents);
  }

  formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  // --- UI HELPER METHODS ---
  setButtonLoading(isLoading) {
    const btn = this.atcBtn;
    if (!btn) return;

    if (isLoading) {
      btn.disabled = true;
      btn.dataset.originalText = btn.innerHTML;
      btn.innerHTML = '<span style="opacity: 0.7;">Adding...</span>';
    } else {
      btn.disabled = false;
      if (btn.dataset.originalText) {
        btn.innerHTML = btn.dataset.originalText;
        // CRITICAL: Re-query for price span after restoring innerHTML
        // The old reference is now pointing to a detached DOM element
        this.atcPriceSpan = this.querySelector('#cpdp-atc-price');
        // Update button to reflect current selection
        this.updateATCButton();
      }
    }
  }

  showCartFallback() {
    let fallbackDiv = this.querySelector('.cpdp-cart-fallback');

    if (!fallbackDiv) {
      fallbackDiv = document.createElement('div');
      fallbackDiv.className = 'cpdp-cart-fallback';
      fallbackDiv.style.cssText = `
        background: #e8f5e9;
        color: #2e7d32;
        padding: 16px;
        border-radius: 8px;
        margin-top: 12px;
        border: 1px solid #4caf50;
        text-align: center;
      `;
      fallbackDiv.innerHTML = `
        <p style="margin: 0 0 12px 0; font-weight: 600;">✓ Added to cart!</p>
        <a href="/cart" style="
          display: inline-block;
          background: #2e7d32;
          color: white;
          padding: 10px 24px;
          border-radius: 20px;
          text-decoration: none;
          font-weight: 600;
        ">View Cart</a>
      `;
      this.atcBtn.parentElement.appendChild(fallbackDiv);
    }

    fallbackDiv.style.display = 'block';
    setTimeout(() => { fallbackDiv.style.display = 'none'; }, 8000);

    // Update cart icon after showing fallback
    this.updateCartIconFromFallback();
  }

  async updateCartIconFromFallback() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      // Update header cart icon
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (!cartIconBubble) return;

      let countBubble = cartIconBubble.querySelector('.cart-count-bubble');

      if (cart.item_count > 0) {
        if (!countBubble) {
          countBubble = document.createElement('div');
          countBubble.className = 'cart-count-bubble';
          countBubble.innerHTML = `
            <span aria-hidden="true">${cart.item_count}</span>
            <span class="visually-hidden">${cart.item_count} items</span>
          `;
          cartIconBubble.appendChild(countBubble);
        } else {
          countBubble.querySelector('span[aria-hidden="true"]').textContent = cart.item_count;
          countBubble.querySelector('.visually-hidden').textContent = `${cart.item_count} items`;
        }
      }
    } catch (error) {
      console.error('Failed to update cart icon:', error);
    }
  }

  showError(message) {
    let errorDiv = this.querySelector('.cpdp-error-message');

    if (!errorDiv) {
      errorDiv = document.createElement('div');
      errorDiv.className = 'cpdp-error-message';
      errorDiv.style.cssText = `
        background: #fee;
        color: #c33;
        padding: 12px 16px;
        border-radius: 8px;
        margin-top: 12px;
        border: 1px solid #fcc;
      `;
      this.atcBtn.parentElement.appendChild(errorDiv);
    }

    errorDiv.textContent = 'Error: ' + message;
    errorDiv.style.display = 'block';
    setTimeout(() => { errorDiv.style.display = 'none'; }, 5000);
  }

  // Simple debounce helper
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // --- QUANTITY SELECTOR ---
  initQuantitySelector() {
    const qtyBtns = this.querySelectorAll('.cpdp-qty-btn');
    if (!this.hiddenQtyInput) return;

    qtyBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        qtyBtns.forEach(b => b.classList.remove('selected'));
        btn.classList.add('selected');
        this.currentQty = parseInt(btn.dataset.qty);
        this.hiddenQtyInput.value = this.currentQty;
        this.updateATCButton();
      });
    });
  }

  // --- PURCHASE OPTIONS (One-Time vs Subscription) ---
  initPurchaseOptions() {
    const allPlanRadios = this.querySelectorAll('input[name="purchase_option_picker"]');
    const planRows = this.querySelectorAll('.cpdp-plan-row');
    const optionCard = this.querySelector('.cpdp-option-card');
    const subContainer = this.querySelector('.cpdp-sub-container');

    if (allPlanRadios.length === 0) return;

    const updatePlan = () => {
      // Remove active class from all rows
      planRows.forEach(row => row.classList.remove('active'));

      // Find the checked radio
      const checkedRadio = this.querySelector('input[name="purchase_option_picker"]:checked');
      if (!checkedRadio) {
        return;
      }

      // Add active class to parent row
      const parentRow = checkedRadio.closest('.cpdp-plan-row');
      if (parentRow) {
        parentRow.classList.add('active');
      }

      // Toggle active-container class on option card and subscription container
      const isOneTime = checkedRadio.closest('.cpdp-option-card') !== null;

      if (optionCard) {
        optionCard.classList.toggle('active-container', isOneTime);
      }
      if (subContainer) {
        subContainer.classList.toggle('active-container', !isOneTime);
      }

      // Get price from data attribute on the radio button
      const planPriceCents = parseInt(checkedRadio.dataset.planPrice, 10);
      const sellingPlanId = checkedRadio.value;

      // Update selling_plan hidden input
      if (this.sellingPlanInput) {
        this.sellingPlanInput.value = sellingPlanId || '';
      }

      // Update current price - use the price from the radio's data attribute
      // This already contains the correct selling plan allocation price from Liquid
      if (!isNaN(planPriceCents) && planPriceCents > 0) {
        this.currentPriceCents = planPriceCents;
      } else {
        // Fallback to one-time price
        this.currentPriceCents = this.oneTimePriceCents;
      }

      this.currentSellingPlanId = sellingPlanId;
      this.updateATCButton();
    };

    // Attach change listeners to all radio buttons
    allPlanRadios.forEach(radio => {
      radio.addEventListener('change', updatePlan);
    });

    // Initialize with current selection
    updatePlan();
  }

  // --- MEDIA GALLERY ---
  initMediaGallery() {
    const mainImage = this.querySelector('[data-main-image]');
    const thumbs = this.querySelectorAll('[data-thumb-target]');
    if (!mainImage) return;
    thumbs.forEach(thumb => {
      thumb.addEventListener('click', () => {
        thumbs.forEach(t => t.classList.remove('active'));
        thumb.classList.add('active');
        mainImage.src = thumb.dataset.mediaUrl;
        mainImage.srcset = thumb.dataset.mediaSrcset;
      });
    });
  }

  initAccordions() {
    const headers = this.querySelectorAll('.cpdp-accordion-header');
    headers.forEach(header => {
      header.addEventListener('click', () => {
        const content = header.nextElementSibling;
        const isOpen = content.classList.contains('open');
        if (isOpen) {
          content.classList.remove('open');
          content.style.maxHeight = null;
        } else {
          content.classList.add('open');
          content.style.maxHeight = content.scrollHeight + "px";
        }
      });
    });
  }

  // Wait for cart functions to be available (handles script loading race condition)
  async waitForCartFunctions(maxWaitMs = 2000) {
    const startTime = Date.now();
    const checkInterval = 50;
    let attempts = 0;

    while (Date.now() - startTime < maxWaitMs) {
      attempts++;

      if (window.openCustomCart && window.refreshCart) {
        console.log(`Cart ready after ${Date.now() - startTime}ms`);
        return true;
      }

      if (attempts % 10 === 0) {
        console.log(`Waiting for cart... (${Date.now() - startTime}ms)`);
      }

      await new Promise(resolve => setTimeout(resolve, checkInterval));
    }

    console.error(`Cart not ready after ${maxWaitMs}ms`);
    return false;
  }

  initAddToCart() {
    const form = this.querySelector('form[action="/cart/add"]');
    if (!form) {
      console.warn('Custom PDP: No form found with action="/cart/add"');
      return;
    }

    let isAddingToCart = false;

    const handleSubmit = async (e) => {
      // Note: preventDefault is called by submitWrapper, not here (needs to be immediate, not debounced)

      if (isAddingToCart) {
        console.log('Already adding to cart, ignoring duplicate click');
        return;
      }

      isAddingToCart = true;
      this.setButtonLoading(true);

      try {
        const formData = new FormData(form);

        // Remove selling_plan if empty - Shopify expects integer or field to be absent
        const sellingPlan = formData.get('selling_plan');
        if (!sellingPlan || sellingPlan === '' || isNaN(parseInt(sellingPlan, 10))) {
          formData.delete('selling_plan');
        }

        const response = await fetch('/cart/add.js', { method: 'POST', body: formData });

        if (response.ok) {
          // Wait for cart functions with extended timeout for slow networks
          const cartReady = await this.waitForCartFunctions(2000);

          if (cartReady && window.openCustomCart) {
            window.openCustomCart();
          } else {
            // Cart drawer not ready, show fallback notification
            this.showCartFallback();
          }

          this.checkCartState();
          this.checkFreeShippingBadges();
        } else {
          const errorData = await response.json();
          this.showError(errorData.description || 'Unknown error');
        }
      } catch (error) {
        console.error('Custom PDP Add to Cart Error:', error);
        this.showError('Network error. Please check your connection.');
      } finally {
        isAddingToCart = false;
        this.setButtonLoading(false);
      }
    };

    // Wrapper that prevents default immediately, then calls debounced handler
    const submitWrapper = (e) => {
      e.preventDefault(); // CRITICAL: Prevent form submission immediately
      e.stopImmediatePropagation(); // Prevent other handlers (like product-form.js) from executing
      debouncedHandler(e);
    };

    // Debounced handler (but preventDefault is already called by wrapper)
    const debouncedHandler = this.debounce((e) => {
      // Remove preventDefault from handleSubmit since it's already prevented
      handleSubmit(e);
    }, 400);

    form.addEventListener('submit', submitWrapper);
  }
}

customElements.define('custom-pdp', CustomPDP);
